import { z } from 'zod';
import { ECGPredictSinusRhythmRequestSchema } from '../ai/schema';
import { UUIDSchema } from '../domain/models';
import type {
  VitalsLatestResultMeasures,
  VitalsLiveDataPayload,
} from '../vitals/schema';

export type PatientID = string;
export type PatientIDs = string[];
export type SensorID = string;
export type Timestamp = number;

export const SubscriptionActionSchema = z.literal('subscribe');
export const SubscriptionParamsSchema = z.object({
  action: SubscriptionActionSchema,
  authToken: z.string(),
  locationId: UUIDSchema.optional(),
});

/**
 * Type of the event used to subscribe to the Vitals data
 */
export type WebsocketSubscriptionParams = z.infer<
  typeof SubscriptionParamsSchema
>;

/**
 * Data broadcast to the client from the websocket api
 */
export type WebsocketSocketData<
  TAction extends string,
  TType extends string,
  TData,
> = {
  /**
   * The action that the data is in response to
   */
  action: TAction;
  /**
   * The message type for the data. This is needed so we can differentiate data from the stream
   */
  type: TType;
  data: TData;
};

export type WebsocketSubscriptionLiveData = WebsocketSocketData<
  'subscribe',
  'live-stream',
  VitalsLiveDataPayload
>;

export interface WebsocketGetPatientShadowParams {
  patientIds: string[];
  /**
   * Reference timestamp for which to get the data for. If not provided will assume now
   */
  referenceTime?: number;
}
export interface WebsocketGetPatientShadowTimeFrameParams {
  patientId: string;
  start: number;
  end: number;
}

export type WebsocketGetPatientShadowResponse = VitalsLiveDataPayload;
export type WebsocketGetPatientShadowTimeFrameResponse =
  VitalsLatestResultMeasures[];

const BaseCommentSchema = z.object({
  ts: z.number(),
  practitionerId: z.string(),
});

export const BaseAlertSchema = z.object({
  patientId: z.string(),
  ts: z.number(),
  type: z.string(),
  value: z.any(),
  comments: z.array(z.any()).optional(),
});

export const createBaseAlertSchema = <
  TType extends z.ZodLiteral<string>,
  TValue extends z.AnyZodObject,
  TComment extends z.AnyZodObject,
>(
  type: TType,
  value: TValue,
  comment: TComment,
) =>
  z.object({
    patientId: z.string(),
    ts: z.number(),
    type,
    value,
    comments: comment.array().optional(),
  });

export const ECGAlertCommentSchema = BaseCommentSchema.extend({
  quality: z.string(),
  classification: z.string(),
  details: z.string().optional(),
});
export type ECGAlertComment = z.infer<typeof ECGAlertCommentSchema>;

const ECGSinusRhythmAlertDataSchema = ECGPredictSinusRhythmRequestSchema.extend(
  {
    sensorId: z.string(),
  },
);

const ECGSinusRhythmSchema = z.literal('ecg-sinus-rhythm');

export const ECGSinusRhythmAlertSchema = createBaseAlertSchema(
  ECGSinusRhythmSchema,
  ECGSinusRhythmAlertDataSchema,
  ECGAlertCommentSchema,
);

export type ECGSinusRhythmAlert = z.infer<typeof ECGSinusRhythmAlertSchema>;

export type WebsocketAlert = z.infer<typeof ECGSinusRhythmAlertSchema>;

export type WebsocketAlertType = z.infer<typeof ECGSinusRhythmSchema>;

export interface WebsocketGetAlertsParams {
  patientIds: string[];
  limit?: number;
}

export type WebsocketGetAlertsResponse = Record<PatientID, WebsocketAlert[]>;

export type WebsocketPatientAlerts = Record<PatientID, WebsocketAlert[]>;

export type WebsocketAlertLiveData = WebsocketSocketData<
  'subscribe',
  'alert',
  WebsocketPatientAlerts
>;

export type WebsocketLiveData =
  | WebsocketSubscriptionLiveData
  | WebsocketAlertLiveData;

export type WebsocketAlertComment = ECGAlertComment;

export type WebsocketAlertAddCommentRequest = {
  patientId: string;
  alertTs: number;
  alertType: WebsocketAlertType;
  comment: WebsocketAlertComment;
};

export type WebsocketAlertAddCommentResponse = {};
